import React from "react";
import AplSettingsSelector from "../../redux/helpers/AplSettingsSelector";
import { useAppDispatch } from "../../redux/hooks";
import {
    getAllSitesRedux,
    getAplLinesRedux,
    getAplRolesRedux,
    getAplUsersRedux,
    getPrintersDepartmentAssignmentsRedux,
    onClearAdminRedux,
    setAddUserPasswordRedux,
    setAddUserSiteId,
    setAddUserUsernameRedux,
    setSelectedAplRoleRedux,
    setSelectedAplUserRedux,
    setSelectedLinesRedux,
    saveUserWorkcentreRoleRedux,
    addUserRedux,
    setUserPasswordReset,
    resetUserPasswordRedux
} from "../../redux/slices/admin.feature";
import { useSelector } from "react-redux";
import { IRootState } from "../../types/redux/IRootStore";
import { userHasPermissionAnyLine } from "../../aplUtils/permissions";
import { UserPermission } from "../../types/userPermission";
import { adminLinesColumns, adminUserWorkcentreRoleUsersColumns } from "./UsersAdminColumns";
import { AplUser } from "../../types/aplUser";
import { DtGrid2 } from "../common/DtGrid2";
import { DtGridPropsOptions2 } from "../../types/dtGridPropsOptions2";
import { ReduxStatePalletSearchSort } from "../../types/redux/reduxStatePalletSearchSort";
import { LineItem } from "../../types/lineItem";
import { ReactSelectStyle } from "../common/rectSelectStyle";
import ReactSelectWrapper from "../common/ReactSeletWrapper";
import { bootboxAlert } from "../common/bootboxWrapper";
import FormInputPassword from "../common/FormInputPassword";
import FormInputText from "../common/FormInputText";
import _ from "lodash";
import { useNonInitialEffect } from "../../hooks/useNonInitialEffect";
import { getExceptionText } from "../../translations/gridTranslationHelper";

const UsersAdmin = () => {
    const aplSettings = AplSettingsSelector();
    const isGlobalAdmin = userHasPermissionAnyLine(aplSettings.permsSets, UserPermission.GlobalPermsAdmin);
    const dispatch = useAppDispatch();
    let adminRedux = useSelector((state: IRootState) => state.admin);

    const [userGridFilters, setUserGridFilters] = React.useState({});
    const [userGridSort, setUserGridSort] = React.useState<ReduxStatePalletSearchSort>({ field: ["userName"], direction: ["asc"] });
    const [userGridPage, setUserGridPage] = React.useState(1);

    const [lineGridFilters, setLineGridFilters] = React.useState({});
    const [lineGridSort, setLineGridSort] = React.useState<ReduxStatePalletSearchSort>({ field: ["userName"], direction: ["asc"] });
    const [lineGridPage, setLineGridPage] = React.useState(1);

    const addNewUser = async () => {
        dispatch(addUserRedux(isGlobalAdmin ? 0 : aplSettings.siteId) as any);
    };

    const usersGridOptions: DtGridPropsOptions2 = {
        data: adminRedux.aplUsers
            .filter(x => x.userId !== aplSettings.userId)
            .map(x => ({ ...x, siteName: adminRedux.sites.find(y => y.siteId === x.siteId)?.siteName })),
        columnData: adminUserWorkcentreRoleUsersColumns(aplSettings.culture),
        defaultSort: {
            field: ["userName"],
            direction: ["asc"]
        },
        selectType: "single",
        colouring: true,
        headerFilters: true,

        setSelectedItems: function (x: AplUser[]) {
            dispatch(setSelectedAplUserRedux(x) as any);
        },
        selectedItems: adminRedux.selectedAplUser,
        tableName: "tblAdminUsers",
        tableClass: "tbl-admin-users height-40rem",
        paging: true,
        showSpinner: false,
        rowId: "userId",
        loading: false,
        pageLength: 20,
        sort: userGridSort,
        filters: userGridFilters,
        setFilters: (x: any) => {
            setUserGridFilters(x);
        },
        setSort: (x: any) => {
            setUserGridSort(x);
        },
        setPage: (x: number) => {
            setUserGridPage(x);
        },
        page: userGridPage
    };

    const linesGridOptions: DtGridPropsOptions2 = {
        data:
            adminRedux.selectedAplUser.length === 0
                ? []
                : adminRedux.lines.map(x => ({
                      ...x,
                      siteName: adminRedux.sites.find(y => y.siteId === x.siteId)?.siteName,
                      departmentName: adminRedux.departments.find(y => y.departmentId === x.departmentId)?.departmentName
                  })),
        columnData: adminLinesColumns,
        defaultSort: {
            field: ["lineId"],
            direction: ["desc"]
        },
        selectType: "checkbox",
        colouring: false,
        headerFilters: true,

        setSelectedItems: function (x: LineItem[]) {
            dispatch(setSelectedLinesRedux(x));
        },
        selectedItems: adminRedux.selectedLines,
        tableName: "tblAdminLines",
        tableClass: "tbl-admin-lines height-40rem",
        paging: true,
        showSpinner: false,
        rowId: "lineId",
        loading: false,
        pageLength: 20,
        sort: lineGridSort,
        filters: lineGridFilters,
        setFilters: (x: any) => {
            setLineGridFilters(x);
        },
        setSort: (x: any) => {
            setLineGridSort(x);
        },
        setPage: (x: number) => {
            setLineGridPage(x);
        },
        page: lineGridPage
    };

    const rolesDropdownData = _.sortBy(
        adminRedux.aplRoles.map((x, y) => ({
            value: x.roleId,
            label: `${x.roleName} (${x.roleId})`
        })),
        x => x.label
    );

    const currentUserRoles = adminRedux.userWorkcentreRoles
        .map(x => ({
            ...x,
            lineDescription: adminRedux.lines.find(y => y.lineId === x.workCentre)?.lineDescription,
            departmentName: adminRedux.lines.find(y => y.lineId === x.workCentre)?.departmentName,
            siteName: adminRedux.sites.find(y => y.siteId === adminRedux.lines.find(z => z.lineId === x.workCentre)?.siteId)?.siteName,
            roleName: adminRedux.aplRoles.find(y => y.roleId === x.roleId)?.roleName
        }))
        .sort(x => x.workCentre);

    const setRoles = async () => {
        if (adminRedux.selectedAplRole.length === 0) {
            bootboxAlert("Please select new role to apply for the user");
            return;
        }

        if (adminRedux.selectedAplUser.length === 0) {
            bootboxAlert("Please select the user");
            return;
        }

        let roleId = adminRedux.selectedAplRole[0].roleId;
        let userId = adminRedux.selectedAplUser[0].userId;

        let roles = adminRedux.selectedLines.map(x => ({ userId: userId, workCentre: x.lineId, roleId: roleId }));
        dispatch(saveUserWorkcentreRoleRedux({ userId: userId, userWorkcentreRoles: roles }) as any);
    };

    const changePassword = async () => {
        if (adminRedux.selectedAplUser.length === 0) {
            bootboxAlert("Please select the user");
            return;
        }

        dispatch(resetUserPasswordRedux(isGlobalAdmin ? 0 : aplSettings.siteId) as any);
    };

    React.useEffect(() => {
        dispatch(setAddUserSiteId(aplSettings.siteId));
        dispatch(isGlobalAdmin ? getAllSitesRedux(0) : (getAllSitesRedux(aplSettings.siteId) as any));
        dispatch(isGlobalAdmin ? getAplUsersRedux(0) : (getAplUsersRedux(aplSettings.siteId) as any));
        dispatch(isGlobalAdmin ? getAplLinesRedux(0) : (getAplLinesRedux(aplSettings.siteId) as any));
        dispatch(
            isGlobalAdmin ? getPrintersDepartmentAssignmentsRedux(0) : (getPrintersDepartmentAssignmentsRedux(aplSettings.siteId) as any)
        );
        dispatch(getAplRolesRedux() as any);
        return () => {
            dispatch(onClearAdminRedux());
        };
    }, [aplSettings.siteId, dispatch, isGlobalAdmin]);

    React.useEffect(() => {
        let userLineRoles = adminRedux.userWorkcentreRoles.map(x => x.workCentre);
        let userLines = adminRedux.lines.filter(x => userLineRoles.includes(x.lineId));
        dispatch(setSelectedLinesRedux(userLines));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminRedux.lines, adminRedux.userWorkcentreRoles]);

    useNonInitialEffect(() => {
        if (adminRedux.error) bootboxAlert(getExceptionText(adminRedux.error, aplSettings.culture), () => {});
    }, [adminRedux.error]);

    return (
        <React.Fragment>
            <div>Users Admin</div>
            <br />
            <div className="input-group">
                <FormInputText
                    name="add-user-username"
                    value={adminRedux.addUserUsername}
                    onChange={x => {
                        dispatch(setAddUserUsernameRedux(x));
                    }}
                    placeholder="New User Name"
                    autoCompleteOff={true}
                />
                <FormInputPassword
                    name="add-user-password"
                    value={adminRedux.addUserPassword}
                    onChange={x => {
                        dispatch(setAddUserPasswordRedux(x));
                    }}
                    placeholder="New User Password"
                />

                <div className="input-group-append">
                    <select
                        onChange={(x: React.ChangeEvent<HTMLSelectElement>) => {
                            dispatch(setAddUserSiteId(x.currentTarget.value));
                        }}
                        className="custom-select"
                        id="select-add-user-site"
                        aria-label="site for new user"
                        value={adminRedux.addUserSiteId}
                    >
                        {adminRedux.sites.map(x => (
                            <option key={x.siteId} value={x.siteId}>
                                {x.siteName}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="input-group-append">
                    <button className="btn btn-outline-secondary" type="button" onClick={addNewUser} id="btn-add-user">
                        AddUser
                    </button>
                </div>
            </div>
            <br />
            <div>
                <div className="row">
                    <div className="col-4">
                        <DtGrid2 options={usersGridOptions} />
                        <br />
                        <div className="row"></div>
                    </div>
                    <div className="col-4">
                        <DtGrid2 options={linesGridOptions} />
                    </div>
                    <div className="col-4">
                        <div style={{ height: "40rem" }} id="admin-users-current-lines-assignment" className="table overflow-auto ">
                            <ReactSelectWrapper
                                id="admin-users-current-user-new-role-dropdown"
                                data={rolesDropdownData}
                                isClearable={false}
                                styles={ReactSelectStyle}
                                onChange={x => {
                                    dispatch(setSelectedAplRoleRedux(adminRedux.aplRoles.filter(y => y.roleId === x.value)));
                                }}
                                value={rolesDropdownData.find(x => x.value === adminRedux.selectedAplRole[0]?.roleId) ?? null}
                                isDisabled={false}
                            />
                            <br />
                            <br />
                            {currentUserRoles.map(x => (
                                <li
                                    key={`${x.userId}-${x.departmentName}-${x.workCentre}-${x.roleId}`}
                                >{`${x.siteName} - ${x.departmentName} - ${x.lineDescription} - ${x.roleName}`}</li>
                            ))}
                        </div>
                        <div>
                            <div className="row view-pager float-right">
                                <div className="pager-wrapper">
                                    <div className="text-center">
                                        <div className="dataTables_paginate paging_simple_numbers" id="tblAdminUsers_paginate">
                                            {adminRedux.selectedAplUser.length === 1 ? (
                                                <ul className="pagination">
                                                    <li>
                                                        <FormInputPassword
                                                            value={adminRedux.userPasswordReset}
                                                            name="admin-user-new-password"
                                                            className="page-link"
                                                            onChange={x => {
                                                                dispatch(setUserPasswordReset(x));
                                                            }}
                                                            placeholder="New password"
                                                        />
                                                    </li>

                                                    <li
                                                        className={`paginate_button page-item ${
                                                            adminRedux.userPasswordReset.length ? "active" : ""
                                                        }`}
                                                    >
                                                        <button
                                                            id="admin-users-btn-apply-new-password"
                                                            className="page-link"
                                                            onClick={changePassword}
                                                        >
                                                            Set new password
                                                        </button>
                                                    </li>

                                                    <li className="paginate_button page-item active">
                                                        <button
                                                            aria-controls="tblAdminUsers"
                                                            className="page-link"
                                                            onClick={setRoles}
                                                            id="admin-users-btn-apply-new-role"
                                                        >
                                                            Apply roles
                                                        </button>
                                                    </li>
                                                </ul>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default UsersAdmin;
